import React, { useContext } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  Icon
} from '@chakra-ui/react';
import { FaUpload, FaChartLine, FaFileExport } from 'react-icons/fa';
import ThemeContext from '../../context/ThemeContext';

const HowItWorks = () => {
  const { themeData } = useContext(ThemeContext);

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const accentColor = themeData?.colors?.accent || '#63B3ED';
  const backgroundColor = themeData?.colors?.background || '#F7FAFC';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';

  // Theme-aware colors
  const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
  const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
  const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
  const cardHeadingColor = isDarkTheme ? '#F7FAFC' : 'gray.900';
  const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
  const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
  const iconColor = isDarkTheme ? accentColor : primaryColor;
  const iconBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(43, 108, 176, 0.1)';

  return (
    <Box as="section" py={{ base: 12, md: 20 }} bg={backgroundColor}>
      <Container maxW="container.xl">
        <Heading
          as="h2"
          size="xl"
          mb={{ base: 8, md: 12 }}
          color={headingTextColor}
          fontWeight="bold"
          textAlign={{ base: 'center', md: 'left' }}
        >
          How it works
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, md: 8 }}>
          {/* Card 1 */}
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="xl"
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            align="start"
            spacing={4}
            transition="all 0.2s"
            _hover={{
              transform: 'translateY(-4px)',
              boxShadow: 'lg',
              borderColor: iconColor
            }}
          >
            <Box
              p={3}
              borderRadius="lg"
              bg={iconBg}
            >
              <Icon as={FaUpload} color={iconColor} boxSize={6} />
            </Box>
            <Text fontWeight="bold" fontSize="lg" color={cardHeadingColor}>
              Upload Client Data
            </Text>
            <Text color={bodyTextColor} fontSize="md" lineHeight="tall">
              Securely upload client portfolio data via CSV or Excel with intelligent field discovery and schema mapping.
            </Text>
            <VStack align="start" spacing={1} pt={2}>
              <Text color={mutedTextColor} fontSize="sm">
                • Auto-detect columns
              </Text>
              <Text color={mutedTextColor} fontSize="sm">
                • Human confirmation workflow
              </Text>
            </VStack>
          </VStack>

          {/* Card 2 */}
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="xl"
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            align="start"
            spacing={4}
            transition="all 0.2s"
            _hover={{
              transform: 'translateY(-4px)',
              boxShadow: 'lg',
              borderColor: iconColor
            }}
          >
            <Box
              p={3}
              borderRadius="lg"
              bg={iconBg}
            >
              <Icon as={FaChartLine} color={iconColor} boxSize={6} />
            </Box>
            <Text fontWeight="bold" fontSize="lg" color={cardHeadingColor}>
              AI Analyzes Biases
            </Text>
            <Text color={bodyTextColor} fontSize="md" lineHeight="tall">
              Our AI engine detects 15 behavioral biases, calculates risk scores, and prioritizes clients for relationship managers.
            </Text>
            <VStack align="start" spacing={1} pt={2}>
              <Text color={mutedTextColor} fontSize="sm">
                • Evidence-based scoring
              </Text>
              <Text color={mutedTextColor} fontSize="sm">
                • Real-time dashboards
              </Text>
            </VStack>
          </VStack>

          {/* Card 3 */}
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="xl"
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            align="start"
            spacing={4}
            transition="all 0.2s"
            _hover={{
              transform: 'translateY(-4px)',
              boxShadow: 'lg',
              borderColor: iconColor
            }}
          >
            <Box
              p={3}
              borderRadius="lg"
              bg={iconBg}
            >
              <Icon as={FaFileExport} color={iconColor} boxSize={6} />
            </Box>
            <Text fontWeight="bold" fontSize="lg" color={cardHeadingColor}>
              Export & Take Action
            </Text>
            <Text color={bodyTextColor} fontSize="md" lineHeight="tall">
              Generate client reports, bias radar charts, and actionable recommendations—with complete audit trails for compliance.
            </Text>
            <VStack align="start" spacing={1} pt={2}>
              <Text color={mutedTextColor} fontSize="sm">
                • PDF/CSV exports
              </Text>
              <Text color={mutedTextColor} fontSize="sm">
                • Compliance-ready reports
              </Text>
            </VStack>
          </VStack>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default HowItWorks;